// Iteration 098 -- Netball League Table page.
// Parallel to rugby-league-table.tsx. Netball is strict 3/1/0 -- no bonus
// points, no try/losing bonuses (SA school convention).
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import type { NetballLeagueTableRow, NetballLeagueTableResponse } from "@dugads/shared";
import * as api from "@/lib/api";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

export default function NetballLeagueTablePage() {
  const { schoolId } = useParams<{ schoolId: string }>();
  const [rows, setRows] = useState<NetballLeagueTableRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!schoolId) return;
    setLoading(true);
    api
      .getNetballLeagueTable(schoolId)
      .then((r: NetballLeagueTableResponse) => setRows(r.rows))
      .catch((e: unknown) => setError(e instanceof api.ApiError ? e.message : "Failed to load"))
      .finally(() => setLoading(false));
  }, [schoolId]);

  function exportCsv() {
    const header = ["Team", "School", "P", "W", "D", "L", "GF", "GA", "Diff", "Pts"].join(",");
    const body = rows
      .map((r) =>
        [
          r.team_display_name,
          r.school_display_name,
          r.played,
          r.won,
          r.drawn,
          r.lost,
          r.goals_for,
          r.goals_against,
          r.goals_diff,
          r.league_points,
        ].join(","),
      )
      .join("\n");
    const blob = new Blob([`${header}\n${body}`], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `netball-league-table-${schoolId}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  if (loading) return <p data-page="netball-league-table" data-state="loading">Loading…</p>;

  return (
    <div className="flex flex-col gap-6" data-page="netball-league-table">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Netball League Table</h1>
        {rows.length > 0 && (
          <button className="text-sm underline" onClick={exportCsv} data-action="export-csv">
            Export CSV
          </button>
        )}
      </div>
      <p className="text-xs text-muted-foreground">SA schools convention: 3 win, 1 draw, 0 loss. No bonus points.</p>
      {error && <p className="text-sm text-destructive" data-error>{error}</p>}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Teams</CardTitle>
        </CardHeader>
        <CardContent>
          {rows.length === 0 ? (
            <p className="text-sm text-muted-foreground">No teams yet.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Team</TableHead>
                  <TableHead className="text-right">P</TableHead>
                  <TableHead className="text-right">W</TableHead>
                  <TableHead className="text-right">D</TableHead>
                  <TableHead className="text-right">L</TableHead>
                  <TableHead className="text-right">GF</TableHead>
                  <TableHead className="text-right">GA</TableHead>
                  <TableHead className="text-right">+/-</TableHead>
                  <TableHead className="text-right font-semibold">Pts</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.team_id}>
                    <TableCell>{r.team_display_name}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.played}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.won}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.drawn}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.lost}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.goals_for}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.goals_against}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.goals_diff}</TableCell>
                    <TableCell className="text-right font-semibold tabular-nums">{r.league_points}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
